import { ApiEndpoints } from "@/lib/apiClient";
import Cookies from "js-cookie";
import { AUTH_COOKIE_NAME } from "@knitknot/shared/constants";
import { useWorkspaceStore } from "@/hooks/state";
import { clearPersistedQueryCache } from "@/lib/queryPersister";

interface AuthenticatedUser {
  id: string;
  email: string;
  first_name: string;
  last_name: string;
  email_verified: boolean;
  created_at: string;
  updated_at: string;
}

interface AuthResponse {
  user: AuthenticatedUser;
  token: string;
  refresh_token?: string;
  // Extended fields for onboarding routing
  workspace_id?: string;
  account_id?: string;
  account_name?: string;
  is_new_user?: boolean;
  onboarding_required?: boolean;
  is_superadmin?: boolean;
  no_workspace?: boolean;
}

export interface AuthResult {
  user: AuthenticatedUser;
  isNewUser: boolean;
  onboardingRequired: boolean;
  noWorkspace: boolean;
}

export interface IAuthService {
  authenticate(code: string, invitationToken?: string): Promise<AuthResult | undefined>;
}

/**
 * Decode the auth JWT from the cookie and return the authoritative
 * users.id (`sub` claim). This is the single source of truth for the
 * current user's id on the client — all hooks that need it should call
 * this instead of keeping their own cache.
 *
 * Returns null if the cookie is missing or the token is unparseable.
 */
export function getUserIdFromJWT(): string | null {
  const token = Cookies.get(AUTH_COOKIE_NAME);
  if (!token) return null;
  try {
    const parts = token.split(".");
    if (parts.length !== 3) return null;
    // JWT payloads are base64url; swap chars and pad before atob.
    const b64 = parts[1].replace(/-/g, "+").replace(/_/g, "/");
    const padded = b64 + "=".repeat((4 - (b64.length % 4)) % 4);
    const payload = JSON.parse(atob(padded));
    return typeof payload.sub === "string" ? payload.sub : null;
  } catch {
    return null;
  }
}

/**
 * Remove auth cookies (access AND refresh token) and all client-cached auth
 * state, including the superadmin flag. Does not navigate.
 */
export function clearAuthState(): void {
  Cookies.remove(AUTH_COOKIE_NAME, { path: "/" });
  Cookies.remove("knitknot_refresh_token", { path: "/" });
  Cookies.remove("user", { path: "/" });

  const store = useWorkspaceStore.getState();
  store.clearUserInfo();
  store.clearWorkspaceId();

  localStorage.clear();
  sessionStorage.clear();
  // Perf plan §4.6 — IndexedDB isn't touched by localStorage/sessionStorage
  // clears above, so the persisted query cache needs its own wipe here.
  clearPersistedQueryCache();
}

/**
 * Decode the auth JWT and return the WorkOS session id (`workos_sid` claim).
 * Sign-out hands this to the backend so it can end the SSO session. Returns
 * null for tokens minted before this claim shipped — sign-out still clears
 * local state, it just can't end the WorkOS session for those.
 */
function getWorkosSidFromJWT(): string | null {
  const token = Cookies.get(AUTH_COOKIE_NAME);
  if (!token) return null;
  try {
    const parts = token.split(".");
    if (parts.length !== 3) return null;
    const b64 = parts[1].replace(/-/g, "+").replace(/_/g, "/");
    const padded = b64 + "=".repeat((4 - (b64.length % 4)) % 4);
    const payload = JSON.parse(atob(padded));
    return typeof payload.workos_sid === "string" ? payload.workos_sid : null;
  } catch {
    return null;
  }
}

/**
 * THE sign-out path. Reads the WorkOS session id, removes the auth cookies +
 * caches, then hands off to the backend `/auth/logout`, which ends the WorkOS
 * SSO session and redirects to the marketing site (knitknot.ai).
 *
 * IMPORTANT — does NOT mutate the zustand store (unlike `clearAuthState`).
 * `ProtectedRoute` re-runs on `workspaceId` change; clearing it synchronously
 * makes ProtectedRoute `navigate("/signin")`, and SignIn then fires its own
 * `window.location` redirect to `/auth/login`, which cancels the logout
 * navigation below and silently re-auths (WorkOS session still alive). Cookie
 * removal isn't reactive, so it's safe; the in-memory store dies on the
 * full-page navigation anyway. `replace()` so the dead app page leaves no
 * back-button entry.
 */
export async function signOut(): Promise<void> {
  const workosSid = getWorkosSidFromJWT();
  const url = workosSid
    ? `${ApiEndpoints.logout}?session_id=${encodeURIComponent(workosSid)}`
    : ApiEndpoints.logout;

  try {
    for (const name of [
      AUTH_COOKIE_NAME,
      "knitknot_refresh_token",
      "workspace_id",
      "account_id",
      "user",
      "user_info",
      "account_info",
      "user_roles",
      "is_superadmin",
    ]) {
      Cookies.remove(name, { path: "/" });
    }
    localStorage.clear();
    sessionStorage.clear();
    // Perf plan §4.6 — wipe the persisted query cache on sign-out too (IndexedDB
    // isn't covered by the localStorage/sessionStorage clears above). Awaited:
    // the redirect below would otherwise cancel the in-flight IndexedDB delete
    // (safety review).
    await clearPersistedQueryCache();
  } finally {
    window.location.replace(url);
  }
}

export const authService: IAuthService = {
  async authenticate(code: string, invitationToken?: string): Promise<AuthResult | undefined> {
    const params = new URLSearchParams({ code });
    if (invitationToken) {
      params.set("invitation_token", invitationToken);
    }
    const response = await fetch(`${ApiEndpoints.authenticate}?${params.toString()}`, {
      method: "GET",
      headers: {
        "Content-Type": "application/json",
      },
    });
    const authData: AuthResponse = await response.json();
    if (!response.ok) {
      const detail = (authData as { detail?: string }).detail;
      throw new Error(detail || `Authentication failed (${response.status})`);
    }
    if (authData) {
      // Remove any existing tokens first to ensure clean state
      Cookies.remove(AUTH_COOKIE_NAME, { path: '/' });
      Cookies.remove("knitknot_refresh_token", { path: '/' });

      // Set access token cookie
      Cookies.set(AUTH_COOKIE_NAME, authData.token, {
        sameSite: 'lax',
        secure: import.meta.env.PROD,
        path: '/',
        expires: 1 // 1 day (match 24h access token)
      });

      // Set refresh token cookie if provided
      if (authData.refresh_token) {
        Cookies.set("knitknot_refresh_token", authData.refresh_token, {
          sameSite: 'lax',
          secure: import.meta.env.PROD,
          path: '/',
          expires: 90 // 90 days
        });
      }

      // Set workspace and account context from extended response
      const store = useWorkspaceStore.getState();

      if (authData.workspace_id) {
        store.setWorkspaceId(authData.workspace_id);
      }
      // The owning account is derived from the active workspace, not stored at
      // login (it drifts when you switch workspaces).

      // Store user display info. The authoritative user_id lives in the
      // JWT's `sub` claim — no need to cache it here.
      store.setUserInfo({
        email: authData.user.email,
        firstName: authData.user.first_name,
        lastName: authData.user.last_name,
      });

      // Set onboarding state
      store.setOnboardingRequired(authData.onboarding_required ?? false);

      // Set superadmin flag
      store.setIsSuperadmin(authData.is_superadmin ?? false);

      return {
        user: authData.user,
        isNewUser: authData.is_new_user ?? false,
        onboardingRequired: authData.onboarding_required ?? false,
        noWorkspace: authData.no_workspace ?? false,
      };
    }
    return undefined;
  },

};
